/**
 * finance-core — Bảng cân đối & thanh khoản (GĐ0B, quyết định 24/10/2026)
 *
 * MỘT định nghĩa tài sản ròng cho toàn app (thay 3 bản lệch nhau của bản cũ):
 *   tài sản ròng = tài sản thanh khoản + tài sản dài hạn
 *                  − dư nợ thẻ tín dụng − nợ vay còn lại
 * - Thẻ tín dụng KHÔNG phải tài sản (số `balance` legacy trên thẻ bị bỏ qua);
 *   dư nợ lấy từ `usedCredit`
 * - Nợ vay: status 'active' VÀ 'overdue' (quá hạn vẫn còn nợ — bản cũ chỉ tính
 *   'active' nên bỏ sót); loại 'paid' và bản ghi di sản type === 'credit'
 *   (usedCredit của ví thẻ đã là sổ duy nhất)
 */

export interface SourceLike {
  type?: string;
  balance?: number;
  usedCredit?: number;
  isEmergencyFund?: boolean;
}
export interface DebtLike {
  id?: string;
  status?: string;
  type?: string;
  remainingBalance?: number;
  /** Khoản vay/trả góp trên thẻ tín dụng: số tiền này ĐÃ nằm trong `usedCredit` của thẻ */
  creditCardId?: string | null;
}

/** Loại ví coi là tài sản dài hạn (giá trị tự khai, ít thanh khoản). */
export const LONG_TERM_TYPES = ['savings', 'investment', 'asset'] as const;

const n = (v: unknown): number => Number(v) || 0;

const isLive = (d: DebtLike) => d.type !== 'credit' && (d.status === 'active' || d.status === 'overdue');
/** Khoản vay còn hiệu lực được gắn với một thẻ tín dụng (vay/trả góp trên thẻ). */
export const isCardLinkedLoan = (d: DebtLike): boolean => isLive(d) && typeof d.creditCardId === 'string' && d.creditCardId !== '';

/** Tổng dư nợ các khoản vay gắn thẻ, theo id thẻ — phần này đã nằm trong dư nợ thẻ. */
export const cardLinkedLoanBalance = (debts: DebtLike[]): Map<string, number> => {
  const m = new Map<string, number>();
  for (const d of debts) if (isCardLinkedLoan(d)) m.set(d.creditCardId as string, (m.get(d.creditCardId as string) || 0) + n(d.remainingBalance));
  return m;
};

/** Dư nợ thẻ không tính phần vay/trả góp đã có khoản vay riêng (để tính lãi/mức tối thiểu không trùng). */
export const cardOwnBalance = (used: number, linked: number): number => Math.max(0, n(used) - n(linked));

export interface BalanceSheet {
  liquid: number;
  longTerm: number;
  totalAssets: number;
  cardDebt: number;
  loanDebt: number;
  totalLiabilities: number;
  netWorth: number;
}

export const balanceSheet = (sources: SourceLike[], debts: DebtLike[]): BalanceSheet => {
  let liquid = 0, longTerm = 0, cardDebt = 0;
  for (const s of sources) {
    if (s.type === 'credit') { cardDebt += n(s.usedCredit); continue; }
    if ((LONG_TERM_TYPES as readonly string[]).includes(s.type ?? '')) longTerm += n(s.balance);
    else liquid += n(s.balance);
  }
  // Khoản vay gắn thẻ đã nằm trong dư nợ thẻ (cardDebt) — không cộng lần hai
  const loanDebt = debts
    .filter(d => isLive(d) && !isCardLinkedLoan(d))
    .reduce((sum, d) => sum + n(d.remainingBalance), 0);
  const totalAssets = liquid + longTerm;
  const totalLiabilities = cardDebt + loanDebt;
  return { liquid, longTerm, totalAssets, cardDebt, loanDebt, totalLiabilities, netWorth: totalAssets - totalLiabilities };
};

/**
 * Quỹ khẩn cấp: ví người dùng ĐÁNH DẤU `isEmergencyFund` nếu có; nếu chưa đánh
 * dấu ví nào thì dùng toàn bộ thanh khoản (xem metrics.liquidBalance — cùng định nghĩa với balanceSheet().liquid).
 */
export const emergencyFundBalance = (sources: SourceLike[], fallbackLiquid: number): number => {
  const flagged = sources.filter(s => s.isEmergencyFund && s.type !== 'credit');
  return flagged.length ? flagged.reduce((sum, s) => sum + n(s.balance), 0) : fallbackLiquid;
};
